import React, { useEffect, useState } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { Divider } from 'semantic-ui-react';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import styled from 'styled-components';
import { RouterProps } from 'react-router';
import {
  ProductDto,
  TariffDto,
  TreatmentOverviewDto,
  TreatmentOverviewSearchRequest,
  TreatmentOverviewSearchResponse
} from 'ts-types/api.types';
import { getAllTariffs } from 'services/tariffServices';
import { noop } from 'utils/functionUtils';
import axios from 'services/http';
import FullScreenPageHeader from 'components/FullScreenPageHeader';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import TreatmentOverviewOptionsSegment from 'components/treatmentoverview/segments/TreatmentOverviewOptionsSegment';
import TreatmentOverviewTablesSegment from 'components/treatmentoverview/segments/TreatmentOverviewTablesSegment';
import { exportTreatmentOverview, getTreatmentOverview } from 'services/treatmentServices';
import { fetchAllProducts } from 'services/productServices';

const StyledPraxSegment = styled(PraxStyledSegment)`
  padding: 0 !important;
  margin: 0 !important;
`;

const ContentDiv = styled.div`
  flex: 1 1 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

interface Props extends AuthConsumerRenderProps, MessageSourceProps, RouterProps {
}

const TreatmentOverview = (props: Props) => {

  const cancelTokenSource = axios.CancelToken.source();
  const {history, getMessage} = props;

  const [searchData, setSearchData] = useState({} as Partial<TreatmentOverviewSearchRequest>);
  const [treatments, setTreatments] = useState([] as TreatmentOverviewDto[]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [tariffs, setTariffs] = useState([] as TariffDto[]);
  const [products, setProducts] = useState([] as ProductDto[]);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  useEffect(() => {
    getAllTariffs(cancelTokenSource)
        .then(response => {
          setTariffs(response);
        })
        .catch(() => {
          setErrorMessage(getMessage("error.general"));
        })
        .finally(noop);

    fetchAllProducts(false, cancelTokenSource)
        .then(response => {
          setProducts(response);
        })
        .catch(() => {
          setErrorMessage(getMessage("error.general"));
        })
        .finally(noop);
  }, []);

  const goToHome = () => {
    history.push("/");
  };

  const onFilterTreatments = (searchResponse: TreatmentOverviewSearchResponse) => {
    setTreatments(searchResponse.treatments);
  };

  const onTreatmentsLoadingChange = (treatmentsLoading: boolean) => {
    setTreatmentsLoading(treatmentsLoading);
  };

  const searchTreatments = (exportToFile?: boolean) => {

    onTreatmentsLoadingChange(true);

    if (exportToFile) {
      exportTreatmentOverview(searchData, cancelTokenSource)
          .catch(noop)
          .finally(() => {
            onTreatmentsLoadingChange(false);
          });
    } else {
      getTreatmentOverview(searchData, cancelTokenSource)
          .then(response => {
            onFilterTreatments(response);
          })
          .catch(() => {
            setErrorMessage(getMessage("error.general"));
          })
          .finally(() => {
            onTreatmentsLoadingChange(false);
          })
    }
  };

  return (
      <ContentDiv>
        <FullScreenPageHeader headerMessage={getMessage("header.navigation.treatmentOverview")}
                              buttonMessage={getMessage("back")}
                              onConfirm={() => goToHome()}
        />

        <StyledPraxSegment>
          {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)} style={{margin: 0}}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
          }
          <TreatmentOverviewOptionsSegment
              searchTreatments={searchTreatments}
              searchData={searchData}
              setSearchData={setSearchData}
              tariffs={tariffs}
              products={products}
          />
          <Divider/>
          <TreatmentOverviewTablesSegment
              treatments={treatments}
              treatmentsLoading={treatmentsLoading}
              tariffs={tariffs}
              onFilterTreatments={onFilterTreatments}
              onTreatmentsLoadingChange={onTreatmentsLoadingChange}
              searchTreatments={searchTreatments}
              setErrorMessage={setErrorMessage}
          />
        </StyledPraxSegment>
      </ContentDiv>
  );

}

export default withAuthContext(withMessages(TreatmentOverview));
